import { useQueryClient } from "@tanstack/react-query";
import { Check, MinusCircle } from "lucide-react";
import { useState } from "react";

import { DrawerShell, ProvenanceBadge } from "@/components/comms";
import { ApiError, useApproveNudge, useDraft, useSkipNudge } from "@/lib/comms";
import { logger } from "@/lib/logger";
import { formatShortDate } from "@/lib/utils";

interface DraftDrawerProps {
  accountId: string;
  open: boolean;
  onClose: () => void;
}

type Outcome = "sent" | "skipped";

export function DraftDrawer({ accountId, open, onClose }: DraftDrawerProps) {
  const queryClient = useQueryClient();
  const approve = useApproveNudge();
  const skip = useSkipNudge();
  // What this operator just did, so the banner can say "just now" — null until
  // they act, after which the refetched draft agrees with it.
  const [justDid, setJustDid] = useState<Outcome | null>(null);
  // 409: someone else resolved it. We don't know which way until the refetch
  // lands, so hold the actions closed with a neutral message meanwhile.
  const [conflict, setConflict] = useState(false);
  const { data: draft, isError: draftError } = useDraft(open && accountId ? accountId : null);

  const resolved: Outcome | null =
    justDid ?? (draft && draft.state !== "pending" ? draft.state : null);
  const pendingRequest = approve.isPending || skip.isPending;
  // 409 is the only non-failure: it means someone else resolved the draft and
  // the conflict path takes over. Every other error must surface inline.
  const isConflict = (error: unknown) => error instanceof ApiError && error.status === 409;
  const sendFailed = approve.isError && !isConflict(approve.error);
  const skipFailed = skip.isError && !isConflict(skip.error);

  const handleClose = () => {
    setJustDid(null);
    setConflict(false);
    onClose();
  };

  const handleConflict = (action: Outcome) => {
    // Already handled elsewhere — show the resolved state from the source of
    // truth and refresh the share views instead of crashing.
    logger.debug("[share]", "nudge already handled", accountId, action);
    setConflict(true);
    void queryClient.invalidateQueries({ queryKey: ["comms", "share"] });
  };

  const run = (action: Outcome) => {
    const mutation = action === "sent" ? approve : skip;
    mutation.mutate(
      { accountId },
      {
        onSuccess: () => {
          logger.debug(
            "[share]",
            action === "sent" ? "nudge approved" : "nudge skipped",
            accountId,
          );
          setJustDid(action);
        },
        onError: (error) => {
          if (error instanceof ApiError && error.status === 409) {
            handleConflict(action);
          } else {
            logger.error(
              "[share]",
              `${action === "sent" ? "approve" : "skip"} nudge failed`,
              error,
            );
          }
        },
      },
    );
  };

  return (
    <DrawerShell open={open} onClose={handleClose} title="Email draft">
      {!draft ? (
        <p className="text-wg-06 text-sm">
          {draftError ? "No draft found for this account." : "Loading draft…"}
        </p>
      ) : (
        <>
          <div className="border-wg-03 bg-wg-01 rounded-xl border p-4">
            <div className="mb-3 flex items-baseline justify-between gap-3">
              <span className="flex items-center gap-2">
                <span className="font-heading text-[15px] font-semibold">{draft.subject}</span>
                {/* Share nudges are a demo-only draft — there's no real-data
                    path for them, so this is always mock (not read from the API). */}
                <ProvenanceBadge provenance="mock" />
              </span>
              <span className="text-wg-06 text-xs tabular-nums">
                {draft.date ? formatShortDate(draft.date) : ""}
              </span>
            </div>
            {/* The body stays visible in every state — operators need to read
                exactly what was, or wasn't, sent on their behalf. */}
            <Banner resolved={resolved} justNow={justDid !== null} conflict={conflict} />
            <div className="text-wg-08 text-sm leading-relaxed whitespace-pre-line">
              {draft.body}
            </div>
          </div>
          {!resolved && !conflict && (
            <>
              <div className="mt-4 flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => run("sent")}
                  disabled={pendingRequest}
                  className="bg-jj-red hover:bg-jj-red-dk cursor-pointer rounded-full px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-60"
                >
                  {approve.isPending ? "Sending…" : "Approve & send"}
                </button>
                <button
                  type="button"
                  onClick={() => run("skipped")}
                  disabled={pendingRequest}
                  className="border-wg-04 text-wg-08 hover:border-wg-06 hover:bg-wg-01 cursor-pointer rounded-full border px-5 py-2.5 text-sm font-semibold disabled:opacity-60"
                >
                  {skip.isPending ? "Skipping…" : "Skip"}
                </button>
              </div>
              {(sendFailed || skipFailed) && (
                <p className="text-risk mt-2 text-xs font-semibold">
                  {sendFailed ? "Sending failed" : "Skipping failed"} — please try again.
                </p>
              )}
              <p className="text-wg-06 mt-4 text-xs leading-relaxed">
                Placeholder draft — the agent generates a tailored email per account for you to
                review before sending. Skipping records the decision without emailing the customer.
              </p>
            </>
          )}
        </>
      )}
    </DrawerShell>
  );
}

interface BannerProps {
  resolved: Outcome | null;
  /** True when this operator just acted, vs. arriving at an already-resolved draft. */
  justNow: boolean;
  conflict: boolean;
}

function Banner({ resolved, justNow, conflict }: BannerProps) {
  if (resolved === "sent") {
    return (
      <div className="text-ok mb-3 flex items-center gap-2 text-sm font-semibold">
        <Check className="size-4 shrink-0" strokeWidth={2.4} />
        {/* Non-"just now" sends include auto-nudges nobody approved, so the
            passive wording has to cover both. */}
        {justNow ? "Approved & sent just now." : "This reminder has been sent."}
      </div>
    );
  }
  if (resolved === "skipped") {
    return (
      <div className="text-wg-07 mb-3 flex items-center gap-2 text-sm font-semibold">
        <MinusCircle className="size-4 shrink-0" strokeWidth={2.2} />
        Skipped — no email sent.
      </div>
    );
  }
  if (conflict) {
    return (
      <div className="text-wg-07 mb-3 text-sm font-semibold">
        This reminder has already been handled.
      </div>
    );
  }
  return null;
}
